/**
 * AppointmentsPage Component (Web Version)
 * Maintains identical functionality from Mobile-client-core AppointmentsScreen.tsx
 * Features: Tab navigation, appointment cards, status indicators, pull-to-refresh
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigation } from '../shared/hooks/useNavigation';
import { ROUTES } from '../shared/navigation';
import BottomNavigation from '../components/BottomNavigation';
import {
  Calendar,
  Clock,
  User,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Calendar as CalendarIcon,
  MapPin,
  LogIn
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { getUserAppointments, cancelAppointment } from '../services/appointmentService';

const AppointmentsPage = () => {
  const { navigate } = useNavigation();
  const { user, isAuthenticated, isGuestUser } = useAuthStore();
  
  // State management (mobile-exact)
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' | 'history'

  // Load appointments on mount and auth changes
  useEffect(() => {
    if (isAuthenticated) {
      loadAppointments();
    }
  }, [isAuthenticated]);

  // Load appointments function
  const loadAppointments = useCallback(async () => {
    try {
      setError(null);
      const data = await getUserAppointments();
      setAppointments(data);
    } catch (err) {
      setError('Erro ao carregar agendamentos. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Pull to refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadAppointments();
    setRefreshing(false);
  }, [loadAppointments]);

  // Mobile-exact appointment filtering logic
  const isUpcomingAppointment = (appointment) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const appointmentDate = new Date(appointment.appointment_date);
    appointmentDate.setHours(0, 0, 0, 0);
    
    const isScheduledOrConfirmed = ['SCHEDULED', 'CONFIRMED'].includes(appointment.status);
    const isTodayOrFuture = appointmentDate >= today;
    
    return isScheduledOrConfirmed && isTodayOrFuture;
  };

  // Filter appointments based on active tab
  const filteredAppointments = appointments.filter(appointment => {
    return activeTab === 'upcoming' 
      ? isUpcomingAppointment(appointment)
      : !isUpcomingAppointment(appointment);
  });

  // Format date (Brazilian format)
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString + 'T00:00:00');
      return date.toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Format time
  const formatTime = (timeString) => {
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    } catch (error) {
      return timeString;
    }
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Get status display info - Luxury Design
  const getStatusInfo = (status) => {
    switch (status) {
      case 'SCHEDULED':
      case 'CONFIRMED':
        return { icon: Clock, color: 'text-blue-600 dark:text-blue-400', bg: 'border-blue-600/20 dark:border-blue-400/20 bg-transparent', text: 'Confirmado' };
      case 'COMPLETED':
        return { icon: CheckCircle, color: 'text-green-600 dark:text-green-400', bg: 'border-green-600/20 dark:border-green-400/20 bg-transparent', text: 'Concluído' };
      case 'CANCELLED':
        return { icon: XCircle, color: 'text-red-600 dark:text-red-400', bg: 'border-red-600/20 dark:border-red-400/20 bg-transparent', text: 'Cancelado' };
      case 'NO_SHOW':
        return { icon: AlertCircle, color: 'text-amber-600 dark:text-amber-400', bg: 'border-amber-600/20 dark:border-amber-400/20 bg-transparent', text: 'Faltou' };
      case 'IN_PROGRESS':
        return { icon: Clock, color: 'text-amber-600 dark:text-amber-400', bg: 'border-amber-600/20 dark:border-amber-400/20 bg-transparent', text: 'Em Andamento' };
      default:
        return { icon: Clock, color: 'text-brand-dark/60 dark:text-brand-light/60', bg: 'border-brand-dark/20 dark:border-brand-light/20 bg-transparent', text: status };
    }
  };

  // Handle appointment cancellation
  const handleCancelAppointment = async (appointmentId) => {
    if (!confirm('Tem certeza que deseja cancelar este agendamento?')) {
      return;
    }

    try {
      await cancelAppointment(appointmentId);
      await loadAppointments(); // Reload appointments
      alert('Agendamento cancelado com sucesso!');
    } catch (error) {
      alert('Erro ao cancelar agendamento. Tente novamente.');
    }
  };

  // Render appointment card - Luxury Design
  const renderAppointmentCard = (appointment) => {
    const statusInfo = getStatusInfo(appointment.status);
    const StatusIcon = statusInfo.icon;
    const isUpcoming = isUpcomingAppointment(appointment);

    return (
      <div
        key={appointment.id}
        className="bg-brand-light dark:bg-brand-dark border border-brand-dark/10 dark:border-brand-light/10 p-5 mb-4"
      >
        {/* Header with service name and status */}
        <div className="flex items-start justify-between mb-4 pb-4 border-b border-brand-dark/10 dark:border-brand-light/10">
          <h3 className="font-serif font-bold text-brand-dark dark:text-brand-light flex-1 text-lg tracking-wide">
            {appointment.service?.name || appointment.service_name}
          </h3>
          <div className={`flex items-center space-x-1.5 px-2.5 py-1 border ${statusInfo.bg}`}>
            <StatusIcon size={12} className={statusInfo.color} strokeWidth={1.5} />
            <span className={`text-[10px] font-medium uppercase tracking-wider ${statusInfo.color}`}>
              {statusInfo.text}
            </span>
          </div>
        </div>

        {/* Professional name */}
        <div className="flex items-center space-x-3 mb-3">
          <User size={16} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
          <span className="text-sm text-brand-dark dark:text-brand-light tracking-wide">
            {appointment.professional?.full_name || appointment.professional_name}
          </span>
        </div>

        {/* Date and time */}
        <div className="flex flex-col space-y-2 mb-3">
          <div className="flex items-center space-x-3">
            <CalendarIcon size={16} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
            <span className="text-sm text-brand-dark dark:text-brand-light capitalize tracking-wide">
              {formatDate(appointment.appointment_date)}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <Clock size={16} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
            <span className="text-sm font-medium text-brand-dark dark:text-brand-light tracking-wide">
              {formatTime(appointment.start_time)}
              {appointment.duration_minutes && (
                <span className="text-xs text-brand-dark/50 dark:text-brand-light/50 ml-2 uppercase">
                  • {appointment.duration_minutes}min
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Price */}
        {appointment.price && (
          <div className="mb-3 pt-3 border-t border-brand-dark/10 dark:border-brand-light/10">
            <span className="text-base font-serif font-bold text-brand-dark dark:text-brand-light">
              {formatPrice(appointment.price)}
            </span>
          </div>
        )}

        {/* Client notes */}
        {appointment.notes_by_client && (
          <div className="mb-3 p-3 border border-brand-dark/10 dark:border-brand-light/10">
            <span className="text-xs text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider">Observações:</span>
            <p className="text-sm text-brand-dark dark:text-brand-light mt-2 leading-relaxed">{appointment.notes_by_client}</p>
          </div>
        )}

        {/* Action buttons for upcoming appointments */}
        {isUpcoming && (
          <div className="flex space-x-2 pt-4 border-t border-brand-dark/10 dark:border-brand-light/10">
            <button
              onClick={() => handleCancelAppointment(appointment.id)}
              className="flex-1 py-3 px-4 bg-transparent border border-red-600 dark:border-red-400 text-red-600 dark:text-red-400 font-medium text-sm uppercase tracking-wider hover:bg-red-600 hover:dark:bg-red-400 hover:text-white hover:dark:text-black transition-all duration-300"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render tab navigation - Luxury Design
  const renderTabNavigation = () => (
    <div className="flex border border-brand-dark/10 dark:border-brand-light/10 mb-8">
      <button
        onClick={() => setActiveTab('upcoming')}
        className={`flex-1 py-3 px-4 font-medium text-sm tracking-wider uppercase transition-all duration-300 ${
          activeTab === 'upcoming'
            ? 'bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark'
            : 'bg-transparent text-brand-dark dark:text-brand-light hover:bg-brand-dark/5 dark:hover:bg-brand-light/5'
        }`}
      >
        Próximos
      </button>
      <button
        onClick={() => setActiveTab('history')}
        className={`flex-1 py-3 px-4 font-medium text-sm tracking-wider uppercase transition-all duration-300 border-l border-brand-dark/10 dark:border-brand-light/10 ${
          activeTab === 'history'
            ? 'bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark'
            : 'bg-transparent text-brand-dark dark:text-brand-light hover:bg-brand-dark/5 dark:hover:bg-brand-light/5'
        }`}
      >
        Histórico
      </button>
    </div>
  );

  // Render empty state - Luxury Design
  const renderEmptyState = () => (
    <div className="text-center py-20">
      <Calendar size={56} className="text-brand-dark/20 dark:text-brand-light/20 mx-auto mb-6" strokeWidth={1.5} />
      <h3 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light mb-3 tracking-wide">
        {activeTab === 'upcoming'
          ? 'Nenhum agendamento próximo'
          : 'Nenhum histórico encontrado'
        }
      </h3>
      <p className="text-brand-dark/60 dark:text-brand-light/60 mb-8 tracking-wide">
        {activeTab === 'upcoming'
          ? 'Você não possui agendamentos futuros. Que tal marcar um horário?'
          : 'Você ainda não possui histórico de agendamentos.'
        }
      </p>
      {activeTab === 'upcoming' && (
        <button
          onClick={() => navigate(ROUTES.CART)}
          className="px-8 py-4 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium text-sm tracking-widest uppercase hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
        >
          Agendar Serviço
        </button>
      )}
    </div>
  );

  // Render loading state - Luxury Design
  const renderLoadingState = () => (
    <div className="text-center py-20">
      <RefreshCw size={36} className="text-brand-dark dark:text-brand-light mx-auto mb-6 animate-spin" strokeWidth={1.5} />
      <p className="text-brand-dark/60 dark:text-brand-light/60 tracking-wider uppercase text-sm">Carregando agendamentos...</p>
    </div>
  );

  // Render error state - Luxury Design
  const renderErrorState = () => (
    <div className="text-center py-20">
      <AlertCircle size={56} className="text-red-600 dark:text-red-400 mx-auto mb-6" strokeWidth={1.5} />
      <h3 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light mb-3 tracking-wide">Erro ao carregar</h3>
      <p className="text-brand-dark/60 dark:text-brand-light/60 mb-8 tracking-wide">{error}</p>
      <button
        onClick={loadAppointments}
        className="px-8 py-4 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium text-sm tracking-widest uppercase hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
      >
        Tentar novamente
      </button>
    </div>
  );

  // Render guest CTA - Prompt to login/register
  const renderGuestCTA = () => (
    <div className="text-center py-20">
      <LogIn size={56} className="text-brand-dark/20 dark:text-brand-light/20 mx-auto mb-6" strokeWidth={1.5} />
      <h3 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light mb-3 tracking-wide">
        Faça login para ver seus agendamentos
      </h3>
      <p className="text-brand-dark/60 dark:text-brand-light/60 mb-8 tracking-wide max-w-sm mx-auto">
        Crie uma conta ou faça login para acompanhar seus agendamentos e histórico.
      </p>
      <div className="flex flex-col space-y-3 max-w-xs mx-auto">
        <button
          onClick={() => navigate(ROUTES.LOGIN)}
          className="w-full px-8 py-4 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium text-sm tracking-widest uppercase hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
        >
          Entrar
        </button>
        <button
          onClick={() => navigate(ROUTES.REGISTER)}
          className="w-full px-8 py-4 bg-transparent text-brand-dark dark:text-brand-light border border-brand-dark/20 dark:border-brand-light/20 font-medium text-sm tracking-widest uppercase hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
        >
          Criar Conta
        </button>
      </div>
    </div>
  );

  // If user is a guest, show login CTA
  if (isGuestUser()) {
    return (
      <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto mobile-scroll">
            {/* Header - Luxury Design */}
            <div className="safe-area-top bg-brand-dark dark:bg-brand-light px-6 py-8 border-b border-brand-light/10 dark:border-brand-dark/10">
              <div>
                <h1 className="text-3xl font-serif font-bold text-brand-light dark:text-brand-dark tracking-wide">Meus Agendamentos</h1>
                <p className="text-brand-light/70 dark:text-brand-dark/70 text-sm tracking-wide mt-1">Seus compromissos e histórico</p>
              </div>
            </div>

            {/* Guest CTA Content */}
            <div className="flex-1 bg-brand-light dark:bg-brand-dark px-6 py-8 min-h-0">
              {renderGuestCTA()}
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto mobile-scroll">
          {/* Header - Luxury Design */}
          <div className="safe-area-top bg-brand-dark dark:bg-brand-light px-6 py-8 border-b border-brand-light/10 dark:border-brand-dark/10">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-serif font-bold text-brand-light dark:text-brand-dark tracking-wide">Meus Agendamentos</h1>
                <p className="text-brand-light/70 dark:text-brand-dark/70 text-sm tracking-wide mt-1">Seus compromissos e histórico</p>
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2.5 border border-brand-light/20 dark:border-brand-dark/20 hover:bg-brand-light/10 dark:hover:bg-brand-dark/10 transition-all duration-300"
              >
                <RefreshCw
                  size={20}
                  className={`text-brand-light dark:text-brand-dark ${refreshing ? 'animate-spin' : ''}`}
                  strokeWidth={1.5}
                />
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 bg-brand-light dark:bg-brand-dark px-6 py-8 min-h-0">
            {renderTabNavigation()}
            
            {isLoading ? (
              renderLoadingState()
            ) : error ? (
              renderErrorState()
            ) : filteredAppointments.length === 0 ? (
              renderEmptyState()
            ) : (
              <div className="space-y-4">
                {filteredAppointments.map(renderAppointmentCard)}
              </div>
            )}
          </div>
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};

export default AppointmentsPage;